import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  Signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ButtonComponent,
  InputComponent,
  IOptionItem,
  ModalData,
  ModalRef,
  SelectComponent,
  SkeletonDirective,
  SvgIconComponent,
  TextAreaComponent,
} from '@ui-lib/components';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { LoadingState } from '../../../../shared/types/loading.type';
import { MaintenanceRequestModalService } from './maintenance-request-modal.service';
import { FacilitySelectComponent } from '../../../../shared/components/facility-select/facility-select.component';
import { EquipmentModelSelectComponent } from '../../../../shared/components/equipment-model-select/equipment-model-select.component';
import { ClientSelectComponent } from '../../../../shared/components/client-select/client-select.component';
import { ServiceCenterSelectComponent } from '../../../../shared/components/service-center-select/service-center-select.component';
import { filter, of, switchMap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CreateMaintenanceRequestsRequest } from '../../../../core/openapi/bff-admin-api';
import { UptimeTimerComponent } from '../../../../shared/components/uptime-timer/uptime-timer.component';
import { shouldDisplayEquipmentUptime } from '../../../../shared/helpers/uptime.helper';
import {
  getEquipmentStatusChipClassName,
  getEquipmentStatusIcon,
  getEquipmentStatusTranslationKey,
} from '../../../../shared/utils/get-equipment-status-data.functions';
import {
  IMaintenanceRequestModalData,
  IMaintenanceRequestModalEquipmentData,
} from './maintenance-request-modal.model';
import { MacAddressPipe } from '../../../../shared/pipes/mac-address.pipe';
import { PermissionService } from '../../../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../../../core/models/user-permissions.enum';

const SERIAL_NUMBER_MAX_LENGTH = 128;

type RequestFormControls = {
  facilityId: FormControl<IOptionItem<string> | null>;
  containerNumber: FormControl<number | null>;
  rack: FormControl<number | null>;
  place: FormControl<number | null>;
  modelId: FormControl<IOptionItem<string> | null>;
  serialNumber: FormControl<string>;
  clientId: FormControl<IOptionItem<string> | null>;
  workerId: FormControl<IOptionItem<string> | null>;
  repairType: FormControl<IOptionItem<number> | null>;
  serviceCenterId: FormControl<IOptionItem<string> | null>;
  warrantyType: FormControl<IOptionItem<number> | null>;
  error: FormControl<IOptionItem<number> | null>;
  description: FormControl<string>;
};

@Component({
  selector: 'mon-maintenance-request-modal',
  imports: [
    AsyncPipe,
    ButtonComponent,
    ClientSelectComponent,
    EquipmentModelSelectComponent,
    FacilitySelectComponent,
    InputComponent,
    ReactiveFormsModule,
    SelectComponent,
    ServiceCenterSelectComponent,
    SkeletonDirective,
    SvgIconComponent,
    TextAreaComponent,
    TranslateModule,
    UptimeTimerComponent,
    MacAddressPipe,
  ],
  templateUrl: './maintenance-request-modal.component.html',
  styleUrl: './maintenance-request-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MaintenanceRequestModalService],
})
export class MaintenanceRequestModalComponent
  extends ModalData<IMaintenanceRequestModalData>
  implements OnInit
{
  private readonly modalRef = inject(ModalRef);
  private readonly modalService = inject(MaintenanceRequestModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  public readonly LoadingState = LoadingState;
  public readonly workersLoadingStatus: Signal<LoadingState> =
    this.modalService.workersLoadingStatus;
  public readonly createRequestLoadingStatus: Signal<LoadingState> =
    this.modalService.createRequestLoadingStatus;
  public readonly maintenanceTypeOptionsLoadingStatus: Signal<LoadingState> =
    this.modalService.maintenanceTypeOptionsLoadingStatus;
  public readonly warrantyOptionsLoadingStatus: Signal<LoadingState> =
    this.modalService.warrantyOptionsLoadingStatus;
  public readonly errorOptionsLoadingStatus: Signal<LoadingState> =
    this.modalService.errorOptionsLoadingStatus;
  public readonly maintenanceTypeOptions$ = this.modalService.maintenanceTypeOptions$;
  public readonly warrantyOptions$ = this.modalService.warrantyOptions$;
  public readonly errorOptions$ = this.modalService.errorOptions$;
  protected readonly canManageMaintenance = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.MaintenanceManage),
    { initialValue: false },
  );
  protected readonly canReadWorkers = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.WorkerRead),
    { initialValue: false },
  );
  protected readonly canReadFacilities = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.FacilityRead),
    { initialValue: false },
  );
  protected readonly canReadEquipmentModels = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.EquipmentModelRead),
    { initialValue: false },
  );
  public readonly workerOptions$ = this.permissionService
    .hasPermission(UserPermissionEnum.WorkerRead)
    .pipe(
      switchMap((canReadWorkers) =>
        canReadWorkers && !this.equipmentData ? this.modalService.workerOptions$ : of([]),
      ),
    );

  protected readonly form = new FormGroup<RequestFormControls>({
    facilityId: new FormControl<IOptionItem<string> | null>(null),
    containerNumber: new FormControl<number | null>(null),
    rack: new FormControl<number | null>(null),
    place: new FormControl<number | null>(null),
    modelId: new FormControl<IOptionItem<string> | null>(null),
    serialNumber: new FormControl<string>('', {
      validators: [Validators.required, Validators.maxLength(SERIAL_NUMBER_MAX_LENGTH)],
      nonNullable: true,
    }),
    clientId: new FormControl<IOptionItem<string> | null>(null),
    workerId: new FormControl<IOptionItem<string> | null>(null),
    repairType: new FormControl<IOptionItem<number> | null>(null, Validators.required),
    serviceCenterId: new FormControl<IOptionItem<string> | null>(null, Validators.required),
    warrantyType: new FormControl<IOptionItem<number> | null>(null, Validators.required),
    error: new FormControl<IOptionItem<number> | null>(null, Validators.required),
    description: new FormControl<string>('', { nonNullable: true }),
  });

  public get isInvalid(): boolean {
    return this.form.invalid;
  }

  protected get equipmentData(): IMaintenanceRequestModalEquipmentData | null {
    return this.modalData?.equipment ?? null;
  }

  public ngOnInit(): void {
    this.configureEquipmentRequestMode();
  }

  public onSubmit(): void {
    if (!this.canManageMaintenance()) return;

    this.form.markAllAsTouched();
    if (this.isInvalid || this.createRequestLoadingStatus() === LoadingState.LOADING) return;

    this.modalService
      .createMaintenanceRequest(this.createRequest())
      .pipe(
        filter((result) => result),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.modalRef.close(true));
  }

  private createRequest(): CreateMaintenanceRequestsRequest {
    const value = this.form.getRawValue();
    const equipment = this.equipmentData;

    if (equipment) {
      return {
        equipmentId: equipment.id,
        maintenanceTypeId: value.repairType?.id ?? 0,
        serviceCenterId: value.serviceCenterId?.id ?? '',
        warrantyTypeId: value.warrantyType?.id ?? 0,
        errorId: value.error?.id ?? 0,
        comment: value.description || null,
      };
    }

    return {
      facilityId: value.facilityId?.id ?? null,
      facilityNameSnapshot: value.facilityId?.label ?? null,
      containerNumberSnapshot: value.containerNumber,
      rackNumberSnapshot: value.rack,
      placeNumberSnapshot: value.place,
      equipmentModelId: value.modelId?.id ?? null,
      equipmentModelNameSnapshot: value.modelId?.label ?? null,
      serialNumber: value.serialNumber,
      clientId: value.clientId?.id ?? null,
      clientNameSnapshot: value.clientId?.label ?? null,
      workerId: value.workerId?.id ?? null,
      workerNameSnapshot: value.workerId?.label ?? null,
      maintenanceTypeId: value.repairType?.id ?? 0,
      serviceCenterId: value.serviceCenterId?.id ?? '',
      warrantyTypeId: value.warrantyType?.id ?? 0,
      errorId: value.error?.id ?? 0,
      comment: value.description || null,
    };
  }

  private configureEquipmentRequestMode(): void {
    const equipment = this.equipmentData;
    if (!equipment) return;

    this.form.controls.serialNumber.clearValidators();
    this.form.controls.serialNumber.setValue('', { emitEvent: false });
    this.form.controls.serialNumber.disable({ emitEvent: false });
    this.form.controls.serialNumber.updateValueAndValidity({ emitEvent: false });
  }

  protected getFirmwareDisplay(equipment: IMaintenanceRequestModalEquipmentData): string {
    const firmwareName = equipment.firmwareName;
    const rawFirmware = equipment.rawFirmware;

    if (firmwareName && rawFirmware) {
      return `${firmwareName} (${rawFirmware})`;
    }

    return firmwareName ?? rawFirmware ?? '-';
  }

  protected readonly shouldDisplayEquipmentUptime = shouldDisplayEquipmentUptime;
  protected readonly getEquipmentStatusChipClassName = getEquipmentStatusChipClassName;
  protected readonly getEquipmentStatusIcon = getEquipmentStatusIcon;
  protected readonly getEquipmentStatusTranslationKey = getEquipmentStatusTranslationKey;
}
